<template>
  <div class="portal-page">
    <!-- Navigation -->
    <header class="navbar" :class="{ 'scrolled': isScrolled }">
      <div class="nav-container">
        <div class="logo">
          <span class="logo-icon"></span>
          SMT PAY
        </div>
        <nav class="menu" :class="{ 'mobile-open': mobileMenuOpen }">
          <a href="https://t.me/rubyoo">Products</a>
          <a href="https://t.me/rubyoo">Solutions</a>
          <a href="https://t.me/rubyoo">Partnership</a>
          <a href="https://t.me/rubyoo">About Us</a>
          <div class="mobile-actions">
            <a href="https://t.me/rubyoo" class="btn-text">Support</a>
            <button class="btn-primary small" @click="goToContact">Get Started</button>
          </div>
        </nav>
        <div class="nav-actions desktop-only">
          <a href="https://t.me/rubyoo" class="btn-text">Support</a>
          <button class="btn-primary small" @click="goToContact">Get Started</button>
        </div>
        <!-- Mobile Menu Toggle -->
        <div class="mobile-toggle" @click="toggleMenu" :class="{ 'active': mobileMenuOpen }">
          <span class="bar"></span>
          <span class="bar"></span>
          <span class="bar"></span>
        </div>
      </div>
    </header>

    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-bg-elements">
        <div class="glow-blob blob-1"></div>
        <div class="glow-blob blob-2"></div>
        <div class="grid-overlay"></div>
      </div>
      <div class="container hero-content">
        <div class="hero-text">
          <div class="badge">Global Payment Infrastructure</div>
          <h1>Financial infrastructure for the internet</h1>
          <p>Millions of companies of all sizes use SMT PAY online and in person to accept payments, send payouts, automate financial processes, and ultimately grow revenue.</p>
          <div class="hero-actions">
            <button class="btn-primary" @click="goToContact">Start now <i class="el-icon-right"></i></button>
            <button class="btn-secondary" @click="goToContact">Contact sales</button>
          </div>
        </div>
        <div class="hero-visual desktop-only">
          <div class="glass-card main-card">
            <div class="card-header">
              <div class="dot red"></div>
              <div class="dot yellow"></div>
              <div class="dot green"></div>
            </div>
            <div class="card-body">
              <div class="balance-label">Total Balance</div>
              <div class="balance-amount">$1,245,000.00</div>
              <div class="chart-container">
                <div class="chart-bar" style="height: 40%"></div>
                <div class="chart-bar" style="height: 60%"></div>
                <div class="chart-bar" style="height: 30%"></div>
                <div class="chart-bar" style="height: 80%"></div>
                <div class="chart-bar" style="height: 50%"></div>
                <div class="chart-bar" style="height: 90%"></div>
                <div class="chart-bar" style="height: 70%"></div>
              </div>
              <div class="recent-transactions">
                <div class="transaction">
                  <div class="tx-icon success"><i class="el-icon-bottom-left"></i></div>
                  <div class="tx-details">
                    <div class="tx-name">Stripe Payout</div>
                    <div class="tx-time">Today, 14:30</div>
                  </div>
                  <div class="tx-amount positive">+$4,500.00</div>
                </div>
                <div class="transaction">
                  <div class="tx-icon"><i class="el-icon-top-right"></i></div>
                  <div class="tx-details">
                    <div class="tx-name">AWS Cloud</div>
                    <div class="tx-time">Yesterday, 09:15</div>
                  </div>
                  <div class="tx-amount">-$340.50</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Stats Section -->
    <section class="stats-section">
      <div class="container stats-grid">
        <div class="stat-item">
          <h3>250M+</h3>
          <p>API requests per day</p>
        </div>
        <div class="stat-item">
          <h3>99.99%</h3>
          <p>Historical uptime</p>
        </div>
        <div class="stat-item">
          <h3>135+</h3>
          <p>Currencies supported</p>
        </div>
        <div class="stat-item">
          <h3>35+</h3>
          <p>Countries with local acquiring</p>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="features-section">
      <div class="container">
        <div class="section-header">
          <h2>A fully integrated suite of payments products</h2>
          <p>We bring together everything that's required to build websites and apps that accept payments and send payouts globally.</p>
        </div>
        <div class="features-grid">
          <div class="feature-card" v-for="(f, i) in features" :key="i">
            <div class="feature-icon">
              <i :class="f.icon"></i>
            </div>
            <h3>{{ f.title }}</h3>
            <p>{{ f.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Security Section -->
    <section class="security-section">
      <div class="container security-content">
        <div class="security-text">
          <h2>Enterprise-grade security</h2>
          <p>Protect your business and your customers with our advanced security infrastructure and compliance certifications. We maintain the highest standards of data protection.</p>
          <ul class="security-list">
            <li><i class="el-icon-circle-check"></i> PCI DSS Level 1 certified</li>
            <li><i class="el-icon-circle-check"></i> End-to-end encryption</li>
            <li><i class="el-icon-circle-check"></i> Advanced fraud protection</li>
            <li><i class="el-icon-circle-check"></i> 24/7 monitoring and support</li>
          </ul>
        </div>
        <div class="security-badges">
          <div class="badge-card">
            <i class="el-icon-lock"></i>
            <span>PCI DSS<br>Level 1</span>
          </div>
          <div class="badge-card">
            <i class="el-icon-document-checked"></i>
            <span>ISO<br>27001</span>
          </div>
          <div class="badge-card">
            <i class="el-icon-data-analysis"></i>
            <span>SOC 2<br>Type II</span>
          </div>
          <div class="badge-card">
            <i class="el-icon-set-up"></i>
            <span>GDPR<br>Compliant</span>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container cta-container">
        <h2>Ready to get started?</h2>
        <p>Join thousands of businesses already using SMT PAY to scale globally.</p>
        <button class="btn-primary large" @click="goToContact">Create an account</button>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="container footer-content">
        <div class="footer-brand">
          <div class="logo">
            <span class="logo-icon"></span>
            SMT PAY
          </div>
          <p>Global Payment Business Solutions</p>
          <p class="footer-desc">Empowering businesses worldwide with seamless, secure, and scalable payment infrastructure.</p>
        </div>
        <div class="footer-links">
          <div class="link-group">
            <h4>Products</h4>
            <a href="#">Payments</a>
            <a href="#">Billing</a>
            <a href="#">Connect</a>
            <a href="#">Payouts</a>
          </div>
          <div class="link-group">
            <h4>Developers</h4>
            <a href="#">Documentation</a>
            <a href="#">API Reference</a>
            <a href="#">Status</a>
            <a href="#">GitHub</a>
          </div>
          <div class="link-group">
            <h4>Company</h4>
            <a href="#">About Us</a>
            <a href="#">Customers</a>
            <a href="#">Careers</a>
            <a href="#">Contact</a>
          </div>
        </div>
      </div>
      <div class="container footer-bottom">
        <p>© {{ currentYear }} SmtPay. All Rights Reserved.</p>
        <div class="footer-legal">
          <a href="#">Privacy Policy</a>
          <a href="#">Terms of Service</a>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'Home',
  data() {
    return {
      isScrolled: false,
      mobileMenuOpen: false,
      features: [
        {
          icon: "el-icon-s-check",
          title: "安全合规 (Secure & Compliant)",
          desc: "国际标准金融合规，保障数据安全与交易可信。PCI DSS Level 1 认证，全链路加密传输。"
        },
        {
          icon: "el-icon-s-promotion",
          title: "高效对接 (Fast Integration)",
          desc: "定制化 API 与系统集成，加速合作流程。提供完善的开发者文档和多语言 SDK。"
        },
        {
          icon: "el-icon-s-cooperation",
          title: "全球合作 (Global Reach)",
          desc: "与世界领先企业携手，共建金融科技新格局。支持 135+ 种货币和多种本地支付方式。"
        },
        {
          icon: "el-icon-s-data",
          title: "数据洞察 (Data Insights)",
          desc: "实时交易监控与多维度数据分析报表，助力企业精准决策，提升运营效率。"
        }
      ],
      revealItems: []
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.handleScroll();
    this.initReveal();
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 20;
      this.checkReveal();
    },
    toggleMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    goToContact() {
      window.location.href = 'https://t.me/rubyoo';
    },
    initReveal() {
      const selectors = ['.stats-section', '.features-section', '.security-section', '.cta-section', '.feature-card'];
      selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          el.classList.add('reveal');
          this.revealItems.push(el);
        });
      });
      this.checkReveal();
    },
    checkReveal() {
      const triggerBottom = window.innerHeight * 0.85;
      this.revealItems.forEach(el => {
        const top = el.getBoundingClientRect().top;
        if (top < triggerBottom) {
          el.classList.add('active');
        }
      });
    }
  }
}
</script>

<style scoped>
/* Design System Variables */
.portal-page {
  --color-primary: #0A2540;
  --color-secondary: #635BFF;
  --color-secondary-hover: #7A73FF;
  --color-accent: #00D4FF;
  --color-bg: #F6F9FC;
  --color-surface: #FFFFFF;
  --color-text-primary: #32325D;
  --color-text-secondary: #6B7C93;
  --color-success: #24B47E;
  --color-danger: #E25950;

  --font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;

  --spacing-xs: 4px;
  --spacing-sm: 8px;
  --spacing-md: 16px;
  --spacing-lg: 24px;
  --spacing-xl: 32px;
  --spacing-xxl: 64px;

  --radius-sm: 4px;
  --radius-md: 8px;
  --radius-lg: 16px;
  --radius-xl: 24px;

  --shadow-sm: 0 2px 5px rgba(50, 50, 93, 0.1), 0 1px 1px rgba(0, 0, 0, 0.07);
  --shadow-md: 0 13px 27px -5px rgba(50, 50, 93, 0.25), 0 8px 16px -8px rgba(0, 0, 0, 0.3);
  --shadow-lg: 0 30px 60px -12px rgba(50, 50, 93, 0.25), 0 18px 36px -18px rgba(0, 0, 0, 0.3);

  font-family: var(--font-family);
  color: var(--color-text-primary);
  background-color: var(--color-bg);
  min-height: 100vh;
  overflow-x: hidden;
}

/* Scroll Animations */
.reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.reveal.active {
  opacity: 1;
  transform: translateY(0);
}

.feature-card.reveal {
  transition-delay: 0.1s;
}

.feature-card:nth-child(2) { transition-delay: 0.2s; }
.feature-card:nth-child(3) { transition-delay: 0.3s; }
.feature-card:nth-child(4) { transition-delay: 0.4s; }

/* Reset & Base */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
}

/* Typography */
h1, h2, h3, h4 {
  color: var(--color-primary);
  font-weight: 700;
  line-height: 1.2;
}

h1 {
  font-size: 4rem;
  letter-spacing: -0.02em;
  margin-bottom: var(--spacing-lg);
}

h2 {
  font-size: 2.5rem;
  letter-spacing: -0.01em;
  margin-bottom: var(--spacing-md);
}

h3 {
  font-size: 1.25rem;
  margin-bottom: var(--spacing-sm);
}

p {
  line-height: 1.6;
  color: var(--color-text-secondary);
}

/* Buttons */
button {
  font-family: inherit;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary {
  background-color: var(--color-secondary);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 30px;
  font-weight: 600;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: var(--shadow-sm);
}

.btn-primary:hover {
  background-color: var(--color-secondary-hover);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.btn-primary.small {
  padding: 8px 16px;
  font-size: 0.9rem;
}

.btn-primary.large {
  padding: 16px 32px;
  font-size: 1.1rem;
}

.btn-secondary {
  background-color: transparent;
  color: var(--color-primary);
  border: 2px solid var(--color-primary);
  padding: 10px 24px;
  border-radius: 30px;
  font-weight: 600;
  font-size: 1rem;
}

.btn-secondary:hover {
  background-color: var(--color-primary);
  color: white;
}

.btn-text {
  background: none;
  border: none;
  color: var(--color-text-primary);
  font-weight: 600;
  text-decoration: none;
}

.btn-text:hover {
  color: var(--color-secondary);
}

/* Navigation */
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  transition: all 0.3s ease;
  padding: 20px 0;
}

.navbar.scrolled {
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  padding: 15px 0;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--color-primary);
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: -0.5px;
}

.logo-icon {
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, var(--color-secondary), var(--color-accent));
  border-radius: 6px;
  display: inline-block;
}

.menu {
  display: flex;
  gap: 32px;
}

.menu a {
  text-decoration: none;
  color: var(--color-text-primary);
  font-weight: 500;
  transition: color 0.2s;
}

.menu a:hover {
  color: var(--color-secondary);
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 24px;
}

.mobile-actions {
  display: none;
}

.mobile-toggle {
  display: none;
  flex-direction: column;
  gap: 5px;
  cursor: pointer;
  z-index: 101;
}

.mobile-toggle .bar {
  width: 24px;
  height: 2px;
  background-color: var(--color-primary);
  transition: all 0.3s;
}

/* Hero Section */
.hero {
  position: relative;
  padding: 160px 0 100px;
  overflow: hidden;
  background-color: var(--color-bg);
}

.hero-bg-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
  pointer-events: none;
}

.glow-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.5;
}

.blob-1 {
  top: -10%;
  right: -5%;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(99,91,255,0.3) 0%, rgba(0,212,255,0) 70%);
}

.blob-2 {
  bottom: -20%;
  left: -10%;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(0,212,255,0.2) 0%, rgba(99,91,255,0) 70%);
}

.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image:
    linear-gradient(to right, rgba(0,0,0,0.03) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(0,0,0,0.03) 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%);
  -webkit-mask-image: linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%);
}

.hero-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 60px;
}

.hero-text {
  flex: 1;
  max-width: 600px;
}

.badge {
  display: inline-block;
  padding: 6px 12px;
  background-color: rgba(99, 91, 255, 0.1);
  color: var(--color-secondary);
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 24px;
}

.hero-text p {
  font-size: 1.25rem;
  margin-bottom: 40px;
}

.hero-actions {
  display: flex;
  gap: 16px;
}

.hero-visual {
  flex: 1;
  display: flex;
  justify-content: center;
  perspective: 1000px;
}

.glass-card {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  width: 100%;
  max-width: 450px;
  transform: rotateY(-10deg) rotateX(5deg);
  transition: transform 0.5s ease;
}

.glass-card:hover {
  transform: rotateY(0) rotateX(0);
}

.card-header {
  padding: 16px;
  border-bottom: 1px solid rgba(0,0,0,0.05);
  display: flex;
  gap: 8px;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}
.dot.red { background-color: #FF5F56; }
.dot.yellow { background-color: #FFBD2E; }
.dot.green { background-color: #27C93F; }

.card-body {
  padding: 32px;
}

.balance-label {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  font-weight: 500;
  margin-bottom: 8px;
}

.balance-amount {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: 32px;
}

.chart-container {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  height: 120px;
  margin-bottom: 32px;
}

.chart-bar {
  flex: 1;
  background: linear-gradient(to top, var(--color-secondary), var(--color-accent));
  border-radius: 4px 4px 0 0;
  opacity: 0.8;
  animation: growBar 2s ease-out forwards;
  transform-origin: bottom;
  transform: scaleY(0);
}

@keyframes growBar {
  to { transform: scaleY(1); }
}

.chart-bar:nth-child(1) { animation-delay: 0.2s; }
.chart-bar:nth-child(2) { animation-delay: 0.3s; }
.chart-bar:nth-child(3) { animation-delay: 0.4s; }
.chart-bar:nth-child(4) { animation-delay: 0.5s; }
.chart-bar:nth-child(5) { animation-delay: 0.6s; }
.chart-bar:nth-child(6) { animation-delay: 0.7s; }
.chart-bar:nth-child(7) { animation-delay: 0.8s; }

.recent-transactions {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.transaction {
  display: flex;
  align-items: center;
  gap: 16px;
}

.tx-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(0,0,0,0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
}

.tx-icon.success {
  background-color: rgba(36, 180, 126, 0.1);
  color: var(--color-success);
}

.tx-details {
  flex: 1;
}

.tx-name {
  font-weight: 600;
  color: var(--color-primary);
  font-size: 0.95rem;
}

.tx-time {
  font-size: 0.8rem;
  color: var(--color-text-secondary);
}

.tx-amount {
  font-weight: 600;
  color: var(--color-primary);
}

.tx-amount.positive {
  color: var(--color-success);
}

/* Stats Section */
.stats-section {
  padding: 60px 0;
  background-color: var(--color-surface);
  border-top: 1px solid rgba(0,0,0,0.05);
  border-bottom: 1px solid rgba(0,0,0,0.05);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  text-align: center;
}

.stat-item h3 {
  font-size: 2.5rem;
  color: var(--color-secondary);
  margin-bottom: 8px;
}

.stat-item p {
  font-weight: 500;
  color: var(--color-text-primary);
}

/* Features Section */
.features-section {
  padding: 120px 0;
  background-color: var(--color-bg);
}

.section-header {
  text-align: center;
  max-width: 700px;
  margin: 0 auto 64px;
}

.section-header p {
  font-size: 1.1rem;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 32px;
}

.feature-card {
  background-color: var(--color-surface);
  padding: 40px 32px;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;
  border: 1px solid rgba(0,0,0,0.03);
}

.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: var(--shadow-md);
}

.feature-icon {
  width: 56px;
  height: 56px;
  background-color: rgba(99, 91, 255, 0.1);
  color: var(--color-secondary);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 24px;
}

.feature-card h3 {
  margin-bottom: 16px;
}

.feature-card p {
  font-size: 0.95rem;
}

/* Security Section */
.security-section {
  padding: 120px 0;
  background-color: var(--color-primary);
  color: white;
}

.security-section h2 {
  color: white;
}

.security-section p {
  color: rgba(255,255,255,0.8);
}

.security-content {
  display: flex;
  align-items: center;
  gap: 80px;
}

.security-text {
  flex: 1;
}

.security-list {
  list-style: none;
  margin-top: 32px;
}

.security-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  font-size: 1.1rem;
  font-weight: 500;
}

.security-list li i {
  color: var(--color-accent);
  font-size: 1.2rem;
}

.security-badges {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.badge-card {
  background-color: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  padding: 32px 24px;
  border-radius: var(--radius-md);
  text-align: center;
  backdrop-filter: blur(10px);
  transition: transform 0.3s;
}

.badge-card:hover {
  transform: translateY(-5px);
  background-color: rgba(255,255,255,0.15);
}

.badge-card i {
  font-size: 2rem;
  color: var(--color-accent);
  margin-bottom: 16px;
  display: block;
}

.badge-card span {
  font-weight: 600;
  font-size: 1.1rem;
  line-height: 1.4;
}

/* CTA Section */
.cta-section {
  padding: 100px 0;
  background-color: var(--color-surface);
  text-align: center;
}

.cta-container h2 {
  margin-bottom: 16px;
}

.cta-container p {
  font-size: 1.2rem;
  margin-bottom: 40px;
}

/* Footer */
.footer {
  background-color: var(--color-bg);
  padding: 80px 0 40px;
  border-top: 1px solid rgba(0,0,0,0.05);
}

.footer-content {
  display: flex;
  justify-content: space-between;
  gap: 64px;
  margin-bottom: 64px;
}

.footer-brand {
  flex: 1;
  max-width: 300px;
}

.footer-brand .logo {
  margin-bottom: 16px;
}

.footer-desc {
  margin-top: 16px;
  font-size: 0.9rem;
}

.footer-links {
  display: flex;
  gap: 80px;
}

.link-group h4 {
  font-size: 1rem;
  margin-bottom: 24px;
}

.link-group a {
  display: block;
  color: var(--color-text-secondary);
  text-decoration: none;
  margin-bottom: 12px;
  font-size: 0.95rem;
  transition: color 0.2s;
}

.link-group a:hover {
  color: var(--color-secondary);
}

.footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 32px;
  border-top: 1px solid rgba(0,0,0,0.1);
  font-size: 0.9rem;
}

.footer-legal {
  display: flex;
  gap: 24px;
}

.footer-legal a {
  color: var(--color-text-secondary);
  text-decoration: none;
}

.footer-legal a:hover {
  color: var(--color-primary);
}

.desktop-only {
  display: flex;
}

/* Responsive Design */
@media (max-width: 1024px) {
  h1 { font-size: 3.5rem; }
  .hero-content { gap: 40px; }
  .stats-grid { grid-template-columns: repeat(2, 1fr); gap: 40px; }
  .security-content { flex-direction: column; gap: 40px; text-align: center; }
  .security-list li { justify-content: center; }
  .footer-content { flex-direction: column; gap: 40px; }
  .footer-links { justify-content: space-between; gap: 40px; }
}

@media (max-width: 768px) {
  h1 { font-size: 2.5rem; }
  h2 { font-size: 2rem; }

  .desktop-only { display: none !important; }

  .mobile-toggle { display: flex; }

  .menu {
    position: fixed;
    top: 70px;
    left: 0;
    width: 100%;
    background-color: var(--color-surface);
    flex-direction: column;
    padding: 24px;
    gap: 24px;
    box-shadow: var(--shadow-md);
    transform: translateY(-150%);
    transition: transform 0.3s ease;
    opacity: 0;
    pointer-events: none;
  }

  .menu.mobile-open {
    transform: translateY(0);
    opacity: 1;
    pointer-events: auto;
  }

  .mobile-actions {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 16px;
    padding-top: 24px;
    border-top: 1px solid rgba(0,0,0,0.1);
  }

  .mobile-toggle.active .bar:nth-child(1) { transform: translateY(7px) rotate(45deg); }
  .mobile-toggle.active .bar:nth-child(2) { opacity: 0; }
  .mobile-toggle.active .bar:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

  .hero { padding: 120px 0 60px; text-align: center; }
  .hero-text { margin: 0 auto; }
  .hero-actions { justify-content: center; flex-direction: column; }
  .hero-actions button { width: 100%; justify-content: center; }

  .stats-grid { grid-template-columns: 1fr; gap: 32px; }

  .features-section, .security-section, .cta-section { padding: 80px 0; }

  .security-badges { grid-template-columns: 1fr; width: 100%; max-width: 300px; margin: 0 auto; }

  .footer-links { flex-direction: column; gap: 32px; }
  .footer-bottom { flex-direction: column; gap: 16px; text-align: center; }
}
</style>



// WEBPACK FOOTER //
// src/views/Home.vue